import React from 'react';
import Select from '../../../components/ui/Select';

const TypographyControls = ({ fontSize, fontFamily, onFontSizeChange, onFontFamilyChange }) => {
  const fontSizeOptions = [
    { value: '12', label: 'Sehr klein (12px)' },
    { value: '13', label: 'Klein (13px)' },
    { value: '14', label: 'Standard (14px)' },
    { value: '15', label: 'Mittel (15px)' },
    { value: '16', label: 'Groß (16px)' },
    { value: '18', label: 'Sehr groß (18px)' }
  ];

  const fontFamilyOptions = [
    { value: 'system', label: 'System Standard', description: 'Verwendet die Standard-Schriftart des Systems' },
    { value: 'inter', label: 'Inter', description: 'Moderne, gut lesbare Sans-Serif Schrift' },
    { value: 'roboto', label: 'Roboto', description: 'Google\'s beliebte Sans-Serif Schrift' },
    { value: 'open-sans', label: 'Open Sans', description: 'Freundliche und offene Sans-Serif Schrift' },
    { value: 'lato', label: 'Lato', description: 'Elegante und professionelle Schrift' },
    { value: 'poppins', label: 'Poppins', description: 'Moderne geometrische Sans-Serif Schrift' },
    { value: 'nunito', label: 'Nunito', description: 'Runde und freundliche Sans-Serif Schrift' }
  ];

  const getFontFamilyCSS = (family) => {
    const fontMap = {
      'system': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      'inter': '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      'roboto': '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
      'open-sans': '"Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      'lato': '"Lato", -apple-system, BlinkMacSystemFont, sans-serif',
      'poppins': '"Poppins", -apple-system, BlinkMacSystemFont, sans-serif',
      'nunito': '"Nunito", -apple-system, BlinkMacSystemFont, sans-serif'
    };
    return fontMap?.[family] || fontMap?.system;
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">Typografie</h3>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Font Size */}
        <div className="space-y-3">
          <Select
            label="Schriftgröße"
            description="Größe des Textes im Chat-Widget"
            options={fontSizeOptions}
            value={fontSize}
            onChange={onFontSizeChange}
          />
        </div>

        {/* Font Family */}
        <div className="space-y-3">
          <Select
            label="Schriftart"
            description="Schriftfamilie für den Chat-Text"
            options={fontFamilyOptions}
            value={fontFamily}
            onChange={onFontFamilyChange}
          />
        </div>
      </div>

      {/* Typography Preview */}
      <div className="p-6 bg-muted rounded-lg">
        <p className="text-xs font-medium text-muted-foreground mb-4">Typografie-Vorschau</p>
        
        <div className="space-y-4">
          {/* Chat Header Preview */}
          <div 
            className="text-lg font-semibold text-foreground"
            style={{ 
              fontSize: `${parseInt(fontSize) + 4}px`,
              fontFamily: getFontFamilyCSS(fontFamily)
            }}
          >
            Chat-Support
          </div>
          
          {/* Message Previews */}
          <div className="space-y-3">
            <div className="bg-card border border-border rounded-lg p-3 max-w-xs">
              <p 
                className="text-foreground"
                style={{ 
                  fontSize: `${fontSize}px`,
                  fontFamily: getFontFamilyCSS(fontFamily)
                }}
              >
                Hallo! Wie kann ich Ihnen heute helfen? Ich bin hier, um alle Ihre Fragen zu beantworten.
              </p>
            </div>
            
            <div className="bg-primary text-primary-foreground rounded-lg p-3 max-w-xs ml-auto">
              <p 
                style={{ 
                  fontSize: `${fontSize}px`,
                  fontFamily: getFontFamilyCSS(fontFamily)
                }}
              >
                Ich hätte gerne Informationen zu Ihren Dienstleistungen.
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-3 max-w-xs">
              <p 
                className="text-foreground"
                style={{ 
                  fontSize: `${fontSize}px`,
                  fontFamily: getFontFamilyCSS(fontFamily)
                }}
              >
                Gerne! Hier sind unsere wichtigsten Services:
              </p>
              <ul 
                className="mt-2 space-y-1 text-muted-foreground"
                style={{ 
                  fontSize: `${parseInt(fontSize) - 1}px`,
                  fontFamily: getFontFamilyCSS(fontFamily)
                }}
              >
                <li>• Webentwicklung</li>
                <li>• Mobile Apps</li>
                <li>• Beratung</li>
              </ul>
            </div>
          </div>
          
          {/* Input Preview */}
          <div className="border-t border-border pt-3">
            <div className="bg-background border border-border rounded-md px-3 py-2">
              <p 
                className="text-muted-foreground"
                style={{ 
                  fontSize: `${fontSize}px`,
                  fontFamily: getFontFamilyCSS(fontFamily)
                }}
              >
                Nachricht eingeben...
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Font Loading Info */}
      <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <div className="w-4 h-4 rounded-full bg-accent flex-shrink-0 mt-0.5"></div>
          <div>
            <p className="text-sm font-medium text-accent-foreground">Schriftart-Hinweis</p>
            <p className="text-xs text-accent-foreground/80 mt-1">
              Benutzerdefinierte Schriftarten werden automatisch geladen. 
              Bei langsamen Verbindungen kann es zu kurzen Verzögerungen kommen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypographyControls;